import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Trash2, Save, X } from 'lucide-react';
import { api, pickLocale, formatApiErrorDetail } from '@/lib/api';

const empty = () => ({
  slug: '', title: { en: '', hi: '', mr: '', gu: '' }, excerpt: { en: '', hi: '', mr: '', gu: '' },
  content: { en: '', hi: '', mr: '', gu: '' }, cover_image: '', author: 'UKGM Team', tags: [], is_published: true,
});

export default function AdminBlogs() {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({ queryKey: ['admin-blog'], queryFn: async () => (await api.get('/blog')).data });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());

  const createMut = useMutation({
    mutationFn: async (d) => (await api.post('/admin/blog', d)).data,
    onSuccess: () => { toast.success('Post created'); qc.invalidateQueries(['admin-blog']); qc.invalidateQueries(['blog']); setEditing(null); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const updateMut = useMutation({
    mutationFn: async ({ id, data }) => (await api.put(`/admin/blog/${id}`, data)).data,
    onSuccess: () => { toast.success('Post updated'); qc.invalidateQueries(['admin-blog']); qc.invalidateQueries(['blog']); setEditing(null); },
  });
  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/blog/${id}`)).data,
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries(['admin-blog']); qc.invalidateQueries(['blog']); },
  });

  const setLoc = (f, lg) => (e) => setForm(x => ({ ...x, [f]: { ...x[f], [lg]: e.target.value } }));
  const setV = (f) => (e) => setForm(x => ({ ...x, [f]: e.target.value }));

  const save = () => {
    const payload = { ...form, tags: Array.isArray(form.tags) ? form.tags : String(form.tags || '').split(',').map(s => s.trim()).filter(Boolean) };
    if (editing === 'new') createMut.mutate(payload);
    else updateMut.mutate({ id: editing, data: payload });
  };

  return (
    <div data-testid="admin-blogs-page">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-3xl text-ukgm-blue font-semibold">Manage News & Blog</h1>
        <button onClick={() => { setForm(empty()); setEditing('new'); }} className="inline-flex items-center gap-2 bg-ukgm-gold text-ukgm-blue font-bold px-5 py-2.5 rounded-full hover:bg-ukgm-goldDark" data-testid="new-blog-btn">
          <Plus className="w-4 h-4" /> New Post
        </button>
      </div>

      {editing && (
        <div className="mt-8 bg-white rounded-2xl border border-ukgm-gold/25 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="font-serif text-lg text-ukgm-blue font-semibold">{editing === 'new' ? 'New Post' : 'Edit Post'}</div>
            <button onClick={() => setEditing(null)}><X className="w-5 h-5 text-ukgm-slate" /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <input placeholder="Slug" value={form.slug} onChange={setV('slug')} className="px-4 py-2.5 rounded-full bg-ukgm-cream text-sm" />
            <input placeholder="Author" value={form.author} onChange={setV('author')} className="px-4 py-2.5 rounded-full bg-ukgm-cream text-sm" />
            <input placeholder="Cover image URL" value={form.cover_image} onChange={setV('cover_image')} className="md:col-span-2 px-4 py-2.5 rounded-full bg-ukgm-cream text-sm" />
            <input placeholder="Tags (comma separated)" value={Array.isArray(form.tags) ? form.tags.join(', ') : form.tags} onChange={e => setForm(x => ({ ...x, tags: e.target.value }))} className="md:col-span-2 px-4 py-2.5 rounded-full bg-ukgm-cream text-sm" />
          </div>
          {['title', 'excerpt', 'content'].map(field => (
            <div key={field}>
              <div className="text-xs tracking-widest uppercase text-ukgm-slate font-bold">{field}</div>
              <div className="mt-2 grid md:grid-cols-2 gap-3">
                {['en', 'hi', 'mr', 'gu'].map(lg => (
                  field === 'content' ? (
                    <textarea key={lg} placeholder={lg.toUpperCase()} rows={4} value={form[field]?.[lg] || ''} onChange={setLoc(field, lg)} className="px-4 py-2.5 rounded-xl bg-ukgm-cream text-sm resize-none" />
                  ) : (
                    <input key={lg} placeholder={lg.toUpperCase()} value={form[field]?.[lg] || ''} onChange={setLoc(field, lg)} className="px-4 py-2.5 rounded-full bg-ukgm-cream text-sm" />
                  )
                ))}
              </div>
            </div>
          ))}
          <button onClick={save} className="inline-flex items-center gap-2 bg-ukgm-blue text-white font-bold px-5 py-2.5 rounded-full" data-testid="save-blog-btn">
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
      )}

      <div className="mt-8 bg-white rounded-2xl border border-ukgm-gold/25 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ukgm-cream">
            <tr className="text-left text-xs uppercase tracking-widest text-ukgm-slate">
              <th className="px-5 py-3">Title</th>
              <th className="px-5 py-3">Author</th>
              <th className="px-5 py-3">Published</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(p => (
              <tr key={p.id} className="border-t border-ukgm-gold/15">
                <td className="px-5 py-3 font-medium text-ukgm-charcoal">{pickLocale(p.title, 'en')}</td>
                <td className="px-5 py-3 text-ukgm-slate">{p.author}</td>
                <td className="px-5 py-3 text-ukgm-slate">{p.is_published ? 'Yes' : 'No'}</td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => { setForm({ ...p, tags: p.tags || [] }); setEditing(p.id); }} className="text-ukgm-blue hover:text-ukgm-gold text-xs font-bold mr-3">EDIT</button>
                  <button onClick={() => window.confirm('Delete?') && deleteMut.mutate(p.id)} className="text-red-600 hover:text-red-700 text-xs font-bold inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> DEL</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
